/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	IConfirmPasswordResetPayload,
	IPasswordResetPayload,
	ISearchUserSendAddRequestPayload,
	IUserPayload,
} from "../../utility/types/userType";
import {
	accountPasswordReset,
	confirmAccountPasswordReset,
	confirmAndVerifyUserAccount,
	fetchAccountVerificationByUserId,
	fetchGetAllUsersAsAdmin,
	fetchUserStats,
	getPresignedImageUrl,
	logOutUser,
	resendVerificationCode,
	signInUserRequest,
	signUpUser,
	submitFetchAllCirclesOfMember,
	submitFetchAllJoinRequestsOfUser,
	submitFetchUserRating,
	submitGetMultipleUsersById,
	submitGetUser,
	submitSearchUserToSendAddRequest,
	updateAccount,
	banUserAsAdmin,
	unbanUserAsAdmin,
	deleteUserAsAdmin,
	restoreUserAsAdmin,
	submitDeleteUserByIdAsAdmin,
	submitGetUserByIdAsAdmin,
	submitUpdateUserByIdAsAdmin,
} from "../services/userService";
import { IVerifyAndConfirmAccountPayload } from "../../components/verifyAndConfirmAccount/types";
import { handleSignInUser, handleSignOutUser } from "../../auth/authenticate";
import { ISignInPayload } from "../../components/signIn/types";
import { setAuthenticationState } from "../reducers/userReducer";

export const signUp = createAsyncThunk(
	"user/signUp",
	async (userData: IUserPayload, { rejectWithValue }) => {
		try {
			const response = await signUpUser(userData);

			return response.data;
		} catch (error: any) {
			if (error.response && error.response.data) {
				return rejectWithValue(error.response.data);
			} else {
				return rejectWithValue(error.message || "Unknown Error Occurred.");
			}
		}
	},
);

export const confirmAndVerifyAccount = createAsyncThunk(
	"user/verify",
	async (
		userVerifyData: IVerifyAndConfirmAccountPayload,
		{ rejectWithValue },
	) => {
		try {
			const response = await confirmAndVerifyUserAccount(userVerifyData);

			return response;
		} catch (error: any) {
			if (error.response && error.response.data) {
				return rejectWithValue(error.response.data);
			} else {
				return rejectWithValue(error.message || "Unknown Error Occurred.");
			}
		}
	},
);

export const resendVerificationCodeAction = createAsyncThunk(
	"user/resend-verification-code",
	async (email: string, { rejectWithValue }) => {
		try {
			const response = await resendVerificationCode(email);
			return response;
		} catch (error: any) {
			if (error.response && error.response.data) {
				return rejectWithValue(error.response.data);
			} else {
				return rejectWithValue(error.message || "Unknown Error Occurred.");
			}
		}
	},
);

export const signIn = createAsyncThunk(
	"auth/signIn",
	async (signInPayload: ISignInPayload, { rejectWithValue }) => {
		try {
			// signIn with amplify
			const user = await handleSignInUser(signInPayload);

			// if amplify sign-in successful, then call backend API
			if (user.isSignedIn) {
				// Check if user was already authenticated with Cognito
				if (user.alreadyAuthenticated) {
					// User is already signed in - call backend to get their data
					const signInData = await signInUserRequest(signInPayload);

					return {
						user: signInData?.user,
						tokens: signInData.tokens,
						alreadyAuthenticated: true,
						accountStatus: signInData?.accountStatus,
						suspensionDetails: signInData?.suspensionDetails,
					};
				}

				const signInData = await signInUserRequest(signInPayload);

				return {
					user: signInData?.user,
					tokens: signInData.tokens,
					// Include suspension info if user can self-resolve
					accountStatus: signInData?.accountStatus,
					suspensionDetails: signInData?.suspensionDetails,
				};
			} else {
				return null;
			}
		} catch (error: any) {
			// Extract serializable error data - Error objects are not serializable
			// and will cause Redux warnings
			if (error instanceof Error) {
				return rejectWithValue({
					message: error.message,
					name: error.name,
				});
			}
			// Handle plain objects (like API error responses)
			if (error && typeof error === "object") {
				return rejectWithValue({
					message: error.message || "Unknown Error Occurred.",
					...error,
				});
			}
			return rejectWithValue({
				message: error?.message || "Unknown Error Occurred.",
			});
		}
	},
);

export const getUser = createAsyncThunk(
	"/user",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await submitGetUser(userId);

			return response.user;
		} catch (error: any) {
			return rejectWithValue(error.message || "Unknown Error Occurred.");
		}
	},
);

export const getMultipleUsersById = createAsyncThunk(
	"/user/etMultipleUsersById",
	async (userIds: string[], { rejectWithValue }) => {
		try {
			const response = await submitGetMultipleUsersById(userIds);
			return response.users;
		} catch (error: any) {
			return rejectWithValue(error.message || "Unknown Error Occurred.");
		}
	},
);

export const logOut = createAsyncThunk(
	"/user/logOut",
	async (_, { dispatch, rejectWithValue }) => {
		try {
			await handleSignOutUser();

			const response = await logOutUser();

			dispatch(setAuthenticationState(false));

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.message || "Unknown Error Occurred.");
		}
	},
);

export const update = createAsyncThunk(
	"/user/update",
	async (
		{ formData, userId }: { formData: FormData; userId: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await updateAccount(formData, userId);

			return response.user;
		} catch (error: any) {
			return rejectWithValue(error.message || "Unknown Error Occurred.");
		}
	},
);

export const passWordReset = createAsyncThunk(
	"/user/password-rest",
	async (email: IPasswordResetPayload, { rejectWithValue }) => {
		try {
			const response = await accountPasswordReset(email);

			const userDetails = response.user[1];

			return userDetails;
		} catch (error: any) {
			const message =
				(typeof error === "string" && error) ||
				error?.message ||
				error?.error ||
				"We couldn't find an account with that email address. Please check and try again.";
			return rejectWithValue(message);
		}
	},
);

export const confirmPassWordReset = createAsyncThunk(
	"/user/confirm-password-rest",
	async (userData: IConfirmPasswordResetPayload, { rejectWithValue }) => {
		try {
			const response = await confirmAccountPasswordReset(userData);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.message || "Unknown Error Occurred.");
		}
	},
);

export const getImageUrl = createAsyncThunk(
	"/user/getPresignedImageUrl",
	async (objectKey: string, { rejectWithValue }) => {
		try {
			const response = await getPresignedImageUrl(objectKey);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.message || "Unknown Error Occurred.");
		}
	},
);

export const fetchSearchUserToSendAddRequest = createAsyncThunk(
	"searchUser",
	async (
		searchValues: ISearchUserSendAddRequestPayload,
		{ rejectWithValue },
	) => {
		try {
			const response = await submitSearchUserToSendAddRequest(searchValues);
			return response.user;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchAllCirclesOfMember = createAsyncThunk(
	"member/circles",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await submitFetchAllCirclesOfMember(userId);

			return response.circles;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchUserRating = createAsyncThunk(
	"user/rating",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await submitFetchUserRating(userId);

			return response.rating;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchAllJoinRequestsOfUser = createAsyncThunk(
	"user/allJoinRequests",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await submitFetchAllJoinRequestsOfUser(userId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.response.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const getAccountVerificationByUserId = createAsyncThunk(
	"verification/getByUserId",
	async (userId: string, { rejectWithValue }) => {
		try {
			const data = await fetchAccountVerificationByUserId(userId);
			return data;
		} catch (error: any) {
			const message = error?.message || "Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const getUserStats = createAsyncThunk(
	"user/stats",
	async (userId: string, { rejectWithValue }) => {
		try {
			const data = await fetchUserStats(userId);
			return data;
		} catch (error: any) {
			const message = error?.message || "Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const getAllUsersAsAdmin = createAsyncThunk(
	"admin/getAllUsers",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchGetAllUsersAsAdmin();

			return response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const banUser = createAsyncThunk(
	"admin/banUser",
	async (
		{ userId, reason }: { userId: string; reason: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await banUserAsAdmin(userId, reason);

			return response.data;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const unbanUser = createAsyncThunk(
	"admin/unbanUser",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await unbanUserAsAdmin(userId);

			return response.data;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const deleteUser = createAsyncThunk(
	"admin/deleteUser",
	async (
		{ userId, reason }: { userId: string; reason: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await deleteUserAsAdmin(userId, reason);

			return response.data;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const restoreUser = createAsyncThunk(
	"admin/restoreUser",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await restoreUserAsAdmin(userId);

			return response.data;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

// ==================== Admin-specific User Management Actions ====================

/**
 * Get a single user by ID (admin only)
 */
export const getUserByIdAsAdmin = createAsyncThunk(
	"admin/getUserById",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await submitGetUserByIdAsAdmin(userId);

			return response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

/**
 * Update user information (admin only)
 */
export const updateUserByIdAsAdmin = createAsyncThunk(
	"admin/updateUserById",
	async (
		{
			userId,
			updateData,
		}: {
			userId: string;
			updateData: {
				firstName?: string;
				lastName?: string;
				email?: string;
				phoneNumber?: string;
				profileImage?: string;
			};
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await submitUpdateUserByIdAsAdmin(userId, updateData);

			return response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

/**
 * Permanently delete a user (admin only)
 */
export const deleteUserByIdAsAdmin = createAsyncThunk(
	"admin/deleteUserById",
	async (
		{ userId, reason }: { userId: string; reason?: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitDeleteUserByIdAsAdmin(userId, reason);

			return response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);
